// Full-page auth layout shared across web and admin apps
// Provides dark gradient background, optional Startus logo, and centered content slot
import type { ReactNode } from 'react';
// Relative import within the package — resolves correctly via Vite alias in all apps
import logoUrl from '../assets/icons/logo-startus.svg';

interface AuthLayoutProps {
  children: ReactNode;
  // Set to false to hide logo (e.g. onboarding steps that share this layout)
  showLogo?: boolean;
  maxWidth?: string;
  // Overrides the height class (default: min-h-screen). Pass "flex-1" in layout contexts
  // where the parent already constrains height and min-h-screen would break flex scroll.
  className?: string;
  // Vertical alignment of the content slot. 'center' (default) is the classic
  // single-card auth look (login, invitations…). 'start' top-anchors content
  // instead — for dashboard-style pages in a flex-1 box (e.g. HubLayout), where
  // centering would float a short page's content in the middle of the screen;
  // the gradient background still fills the box either way, so there's no gap.
  align?: 'center' | 'start';
}

export function AuthLayout({
  children,
  showLogo = true,
  maxWidth = 'max-w-4xl',
  className,
  align = 'center',
}: AuthLayoutProps) {
  return (
    // data-theme="dark" auto-darkens all DaisyUI primitives (inputs, dividers, buttons)
    <div
      data-theme="dark"
      className={`${className ?? 'min-h-screen'} flex flex-col items-center ${align === 'start' ? 'justify-start' : 'justify-center'} px-6 py-12`}
      style={{
        background:
          'radial-gradient(ellipse 175% 80% at 100% 0%, #ad1103 0%, #1a0000 45%, #0d0208 70%)',
      }}
    >
      {/* Brand logo — conditionally rendered; hidden for onboarding steps */}
      {showLogo && (
        <img src={logoUrl} alt="Startus" className="w-44 mb-10 select-none" draggable={false} />
      )}

      {/* Constrained content slot — matches login card width */}
      <div className={`w-full ${maxWidth ?? 'max-w-2xl'} mx-auto`}>{children}</div>
    </div>
  );
}
